import { useState, useCallback, useMemo } from "react";
import type { Party } from "@/types/ui-models";
import { usePartiesList } from "@/hooks";
import { NoRealData } from "@/components/shared";
import PartiesKpiCard from "./components/PartiesKpiCard";
import PartiesFilters from "./components/PartiesFilters";
import PartiesTable from "./components/PartiesTable";
import PartyCard from "./components/PartyCard";
import PartyDetailDrawer from "./components/PartyDetailDrawer";
import { isTechnicalResponsible, norm, splitRoles, uniqueSorted } from "./lib/partyUtils";

const tabs = [
  { id: "cadastro", label: "Cadastro de Partes" },
  { id: "papeis", label: "Papéis e Vínculos" },
  { id: "tecnicos", label: "Responsáveis Técnicos" },
  { id: "orgaos", label: "Órgãos e Instituições" },
  { id: "empresas", label: "Empresas e Contratantes" },
  { id: "projetos", label: "Vínculos por Projeto" },
  { id: "ambientais", label: "Vínculos por Unidade Ambiental" },
  { id: "relatorios", label: "Relatórios" },
];

const initialFilters = {
  search: "",
  type: "",
  role: "",
  status: "",
  municipality: "",
  hasDocs: "",
  hasLink: "",
  isTech: "",
};

/** Abas cujo conteudo depende de dados que o servidor ainda nao expoe. */
const pendingTabs: Record<string, { title: string; description: string; icon: string }> = {
  papeis: {
    title: "Papéis e vínculos",
    description: "Lista de cada vínculo (parte, papel, objeto, vigência e status). O servidor expõe hoje apenas a contagem de vínculos por parte, não os vínculos individuais.",
    icon: "ri-links-line",
  },
  orgaos: {
    title: "Órgãos e instituições",
    description: "Cadastro de prefeituras, secretarias e órgãos ambientais com seus responsáveis. Ainda não há fonte real dedicada.",
    icon: "ri-government-line",
  },
  empresas: {
    title: "Empresas e contratantes",
    description: "Consultorias, executoras e contratantes com contratos e projetos. Ainda não há fonte real dedicada.",
    icon: "ri-building-line",
  },
  projetos: {
    title: "Vínculos por projeto",
    description: "Partes envolvidas em cada projeto e seus papéis. O servidor ainda não expõe os vínculos parte–projeto individualmente.",
    icon: "ri-folder-user-line",
  },
  ambientais: {
    title: "Vínculos por unidade ambiental",
    description: "Partes responsáveis por cada unidade espacial (área verde, UC, exemplar). O servidor ainda não expõe esses vínculos individualmente.",
    icon: "ri-leaf-line",
  },
  relatorios: {
    title: "Relatórios e qualidade do cadastro",
    description: "Relatórios de partes e métricas de qualidade do cadastro. Ainda não há geração de relatórios nem métricas reais para esta seção.",
    icon: "ri-file-chart-line",
  },
};

export default function Partes() {
  const [activeTab, setActiveTab] = useState("cadastro");
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [showCards, setShowCards] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const { items: parties, loading, error } = usePartiesList();

  const handleFiltersChange = useCallback(
    (next: Record<string, string>) => setFilters((prev) => ({ ...prev, ...next })),
    []
  );

  const handleView = useCallback((party: Party) => setSelectedParty(party), []);
  const handleCloseDrawer = useCallback(() => setSelectedParty(null), []);

  const typeOptions = useMemo(() => uniqueSorted(parties.map((p) => p.type)), [parties]);
  const roleOptions = useMemo(() => uniqueSorted(parties.flatMap((p) => splitRoles(p.primaryRole))), [parties]);
  const statusOptions = useMemo(() => uniqueSorted(parties.map((p) => p.status)), [parties]);

  const filteredParties = useMemo(() => {
    return parties.filter((p) => {
      if (filters.search) {
        const s = norm(filters.search);
        if (
          !norm(p.name).includes(s) &&
          !norm(p.institution).includes(s) &&
          !p.documentId.includes(filters.search) &&
          !norm(p.email).includes(s) &&
          !p.phone.includes(filters.search)
        ) return false;
      }
      if (filters.type && p.type !== filters.type) return false;
      if (filters.role && !splitRoles(p.primaryRole).includes(filters.role)) return false;
      if (filters.status && p.status !== filters.status) return false;
      if (filters.municipality) {
        const m = norm(filters.municipality);
        if (!norm(p.municipality).includes(m) && norm(p.uf) !== m) return false;
      }
      if (filters.hasDocs === "yes" && !p.hasDocuments) return false;
      if (filters.hasLink === "yes" && p.activeLinks === 0) return false;
      if (filters.isTech === "yes" && !isTechnicalResponsible(p)) return false;
      return true;
    });
  }, [filters, parties]);

  const technicalResponsibles = useMemo(() => parties.filter(isTechnicalResponsible), [parties]);
  const withLinks = parties.filter((p) => p.activeLinks > 0).length;
  const withoutDocs = parties.filter((p) => !p.hasDocuments).length;

  const kpi = (n: number) => (loading || error ? "—" : n.toLocaleString("pt-BR"));
  const pending = pendingTabs[activeTab];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Partes Envolvidas</div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
          Partes Envolvidas
        </h1>
        <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
          Gerencie pessoas, órgãos, empresas e responsáveis vinculados ao CTA, projetos ambientais, documentos, autorizações e obrigações.
        </p>
      </div>

      {/* Conceptual notice */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-secondary-600 w-full h-full flex items-center justify-center text-lg" />
          </div>
          <p className="text-xs text-secondary-700 leading-relaxed">
            As partes envolvidas representam <strong>pessoas, instituições ou empresas</strong> que possuem responsabilidade, vínculo técnico, obrigação, autorização ou relação documental com unidades ambientais do FloraIQ — conforme modelo <strong>LADM-FloraIQ</strong>: Party → Role → Source → Spatial Unit → RRR.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <PartiesKpiCard label="Partes cadastradas" value={kpi(parties.length)} icon="ri-team-line" color="primary" subtitle="Total no sistema" />
        <PartiesKpiCard label="Responsáveis técnicos" value={kpi(technicalResponsibles.length)} icon="ri-medal-line" color="primary" subtitle="Com papel de responsável técnico" />
        <PartiesKpiCard label="Partes com vínculo" value={kpi(withLinks)} icon="ri-link-m" color="secondary" subtitle="Com projeto ou unidade espacial" />
        <PartiesKpiCard label="Partes sem documento" value={kpi(withoutDocs)} icon="ri-file-warning-line" color="warning" subtitle="Precisam de documentação" />
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {pending ? (
          <NoRealData title={pending.title} description={pending.description} icon={pending.icon} />
        ) : loading ? (
          <div className="text-center py-12 text-foreground-400 text-sm">Carregando partes...</div>
        ) : error ? (
          <NoRealData
            title="Não foi possível carregar as partes"
            description="A consulta ao servidor falhou. Tente novamente em instantes."
            icon="ri-error-warning-line"
          />
        ) : parties.length === 0 ? (
          <NoRealData
            title="Nenhuma parte cadastrada"
            description="O cadastro lista as pessoas, órgãos e empresas registrados no banco. Ainda não há nenhum."
            icon="ri-team-line"
          />
        ) : activeTab === "tecnicos" ? (
          technicalResponsibles.length === 0 ? (
            <NoRealData
              title="Nenhum responsável técnico"
              description="Partes com o papel de responsável técnico aparecem aqui. Nenhuma parte cadastrada tem esse papel."
              icon="ri-medal-line"
            />
          ) : (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground-900">Responsáveis técnicos</h3>
              <PartiesTable parties={technicalResponsibles} onView={handleView} />
            </div>
          )
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">Cadastro de partes</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCards(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-table-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Tabela
                </button>
                <button
                  onClick={() => setShowCards(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showCards ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-grid-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Cards
                </button>
              </div>
            </div>

            <PartiesFilters
              filters={filters}
              onChange={handleFiltersChange}
              typeOptions={typeOptions}
              roleOptions={roleOptions}
              statusOptions={statusOptions}
            />

            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-filter-line w-4 h-4 flex items-center justify-center" />
              <span>{filteredParties.length} de {parties.length} partes encontradas</span>
            </div>

            {filteredParties.length === 0 ? (
              <div className="text-center py-12 text-foreground-400">
                <i className="ri-user-search-line text-4xl mb-3 block" />
                <p className="text-sm">Nenhuma parte encontrada com os filtros atuais.</p>
              </div>
            ) : showCards ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredParties.map((party) => (
                  <PartyCard key={party.id} party={party} onView={handleView} />
                ))}
              </div>
            ) : (
              <PartiesTable parties={filteredParties} onView={handleView} />
            )}
          </div>
        )}
      </div>

      {/* Drawer */}
      {selectedParty && (
        <PartyDetailDrawer party={selectedParty} onClose={handleCloseDrawer} />
      )}
    </div>
  );
}
